import React, { useEffect, useMemo, useState } from "react";
import { Calendar, CornerDownRight, ListPlus, X } from "lucide-react";

// Strip common list prefixes: "- ", "* ", "• ", "1. ", "1) ", "[ ] ", "[x] ", "☐ ".
const PREFIX_RE = /^(?:[-*•▪◦–—]+|\d+[.)]|\[[ xX]\]|☐|☑|✓)\s*/;

/**
 * Parse pasted multi-line text into goals. Indented lines (2+ spaces or a tab)
 * become sub-goals of the previous top-level line when `nested` is true.
 */
export function parseBulkGoals(raw, { nested = true } = {}) {
  const items = [];
  const lines = String(raw || "").replace(/\r/g, "").split("\n");
  for (const line of lines) {
    if (!line.trim()) continue;
    const indent = (line.match(/^[\t ]*/) || [""])[0].replace(/\t/g, "  ").length;
    let text = line.trim().replace(PREFIX_RE, "").trim();
    // A second pass catches "- [ ] task" style combos.
    text = text.replace(PREFIX_RE, "").trim();
    if (!text) continue;
    if (nested && indent >= 2 && items.length > 0) {
      items[items.length - 1].subGoals.push(text);
    } else {
      items.push({ text, subGoals: [] });
    }
  }
  return items;
}

/**
 * Preview + confirm dialog shown when a multi-line list is pasted into the
 * "Add a new goal" box. The text stays editable here so stray lines can be
 * fixed before adding. One optional deadline applies to everything added.
 */
const BulkAddGoalsModal = ({ isOpen, rawText, tabName, onConfirm, onClose }) => {
  const [text, setText] = useState(rawText || "");
  const [nested, setNested] = useState(true);
  const [deadline, setDeadline] = useState("");

  useEffect(() => {
    if (isOpen) {
      setText(rawText || "");
      setNested(true);
      setDeadline("");
    }
  }, [isOpen, rawText]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  const items = useMemo(() => parseBulkGoals(text, { nested }), [text, nested]);
  const subCount = items.reduce((n, it) => n + it.subGoals.length, 0);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-gray-800 rounded-3xl border-2 border-cyan-500/30 shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add multiple goals"
      >
        <div className="flex items-center justify-between gap-3 p-5 border-b border-gray-700">
          <div className="flex items-center gap-2 min-w-0">
            <ListPlus className="w-5 h-5 text-yellow-400 flex-shrink-0" />
            <h3 className="text-lg font-bold text-yellow-400 truncate">
              Add multiple goals
              {tabName ? (
                <span className="text-cyan-300 font-normal text-sm"> · to {tabName}</span>
              ) : null}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="tap p-2 rounded-full hover:bg-gray-700 text-gray-400 hover:text-cyan-300"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Pasted list (edit if needed)
              </label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={10}
                className="w-full bg-gray-700 text-cyan-200 px-3 py-2 rounded-2xl border-2 border-gray-600 focus:border-yellow-500 outline-none text-sm leading-relaxed font-mono"
                spellCheck={false}
              />
            </div>
            <div className="min-w-0">
              <label className="block text-cyan-300 text-sm font-semibold mb-2">
                Preview · {items.length} goal{items.length === 1 ? "" : "s"}
                {subCount > 0 ? ` + ${subCount} sub-goal${subCount === 1 ? "" : "s"}` : ""}
              </label>
              <div className="bg-gray-700/50 rounded-2xl border-2 border-gray-600 p-3 max-h-64 overflow-y-auto space-y-1.5">
                {items.length === 0 ? (
                  <p className="text-sm text-gray-400">Nothing to add yet.</p>
                ) : (
                  items.map((it, i) => (
                    <div key={i}>
                      <div className="text-sm text-cyan-200 truncate">• {it.text}</div>
                      {it.subGoals.map((s, j) => (
                        <div
                          key={j}
                          className="text-xs text-orange-300 pl-4 flex items-center gap-1 truncate"
                        >
                          <CornerDownRight className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{s}</span>
                        </div>
                      ))}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6">
            <label className="flex items-center gap-2 text-sm text-cyan-200 cursor-pointer">
              <input
                type="checkbox"
                checked={nested}
                onChange={(e) => setNested(e.target.checked)}
                className="w-4 h-4 accent-cyan-500"
              />
              Indented lines become sub-goals
            </label>
            <label className="flex items-center gap-2 text-sm text-cyan-200">
              <Calendar className="w-4 h-4 text-cyan-400" />
              Deadline for all (optional)
              <input
                type="date"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="bg-gray-700 text-cyan-200 px-2 py-1 rounded-xl border-2 border-gray-600 focus:border-yellow-500 outline-none text-sm"
              />
            </label>
          </div>
        </div>

        <div className="p-5 border-t border-gray-700 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={onClose}
            className="tap px-5 py-2.5 rounded-2xl bg-gray-700 text-gray-300 hover:bg-gray-600 font-semibold"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={items.length === 0}
            onClick={() => onConfirm(items, deadline || null)}
            className="tap px-5 py-2.5 rounded-2xl bg-yellow-400 hover:bg-yellow-300 disabled:bg-gray-600 disabled:cursor-not-allowed text-black font-semibold flex items-center gap-2"
          >
            <ListPlus className="w-4 h-4" />
            Add {items.length} goal{items.length === 1 ? "" : "s"}
            {subCount > 0 ? ` (+${subCount} sub)` : ""}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkAddGoalsModal;
